// Loose shapes on purpose: the dialog holds a hydrated `Product` / `BcmDynamicPrice`, the specs and
// the booking payloads only carry the fields below.
export interface DynamicPriceRuleMapSource {
    ruleSets?: unknown[] | null;
    calculationSets?: unknown[] | null;
    groups?: unknown[] | null;
}

export interface DynamicPriceProductSource {
    priceRuleMap?: DynamicPriceRuleMapSource | null;
}

export interface DynamicPriceResultSource {
    ruleIndex?: number | null;
    ruleEvaluations?: { evaluations?: unknown[] } | null;
    ruleCalculations?: { calculations?: unknown[], groups?: { isImplicitFallback?: boolean }[] } | null;
}

/** How many *selecting* conditions a product carries; `groupCount === null` means the legacy engine. */
export interface DynamicPriceRuleShape {
    ruleSetCount: number;
    groupCount: number | null;
}

export enum DynamicPriceState {
    /** The product carries no evaluable rules at all — nothing to configure, nothing to compute. */
    NoRules = 'noRules',
    /** Rules exist, but the evaluation produced no rule, formula or group row. */
    NoMatch = 'noMatch',
    /** The evaluation produced something the panel can render. */
    Resolved = 'resolved',
}

/**
 * Why the dynamic price panel has nothing to show. Without this the panel renders a bare toggle
 * whenever a product is flagged `hasDynamicPrice` but its rule map yields nothing — no criteria
 * fields, no evaluation, no explanation (#6944).
 *
 * `hasEvaluableRules()` is the same condition `_validateRules()` guards on, so the message and the
 * evaluation cannot drift apart.
 */
export function resolveDynamicPriceState(
    product: DynamicPriceProductSource | null | undefined,
    dynamicPrice: DynamicPriceResultSource | null | undefined,
): DynamicPriceState {

    if (!hasEvaluableRules(product)) {
        return DynamicPriceState.NoRules;
    }

    // `ruleIndex` is the engine's own "a rule set matched" signal (null when none did) and must be
    // checked on its own: a catch-all rule set - `rules: []`, which the rule builder creates by
    // default - matches and sets ruleIndex/rulePrice while applyRuleEvaluations() walks its empty
    // rules array and returns no evaluations. Going by row counts alone reported "no rule applies"
    // on a position the engine had just priced correctly.
    const hasResult = dynamicPrice?.ruleIndex != null
        || dynamicPrice?.ruleEvaluations?.evaluations?.length > 0
        || dynamicPrice?.ruleCalculations?.calculations?.length > 0
        || dynamicPrice?.ruleCalculations?.groups?.length > 0;

    return hasResult ? DynamicPriceState.Resolved : DynamicPriceState.NoMatch;
}

/**
 * Whether the product's *conditions* were all evaluated to false, i.e. the price on the result is
 * the catalog fallback rather than a rule price. Sharper than DynamicPriceState.NoMatch on purpose:
 * that one asks "is there anything to render", and answers yes for a miss, because the engine still
 * emits a formula row and a synthesized group row. Here a miss must stay a miss — a booking must not
 * bill a product whose rules exclude the boat. (#7017)
 *
 * A product whose price is *computed* is never a miss, even when its conditions all failed:
 * `applyRuleCalculations()` runs no matter what `ruleIndex` says and multiplies its factors onto the
 * catalog price, so the result is a real price and not the untouched fallback. That covers both the
 * formula-only product and the one carrying conditions *and* a formula — the dev seed's
 * "Gastliegeplatzgebühr pro Meter" is the latter.
 */
export function hasUnmatchedPriceRules(
    product: DynamicPriceProductSource | null | undefined,
    dynamicPrice: DynamicPriceResultSource | null | undefined,
): boolean {

    const ruleMap = product?.priceRuleMap;

    return hasUnmatchedPriceRuleShape(
        {
            ruleSetCount: ruleMap?.ruleSets?.length ?? 0,
            groupCount: ruleMap?.groups != null ? ruleMap.groups.length : null,
        },
        dynamicPrice,
    );
}

/**
 * Same verdict from the counted shape alone, for callers that hold no hydrated product — the
 * booking-v2 wizard maps its relation products to slim DTOs and would otherwise have to carry the
 * whole rule map through its state. `groupCount === null` means "legacy engine". (#7017)
 */
export function hasUnmatchedPriceRuleShape(
    shape: DynamicPriceRuleShape | null | undefined,
    dynamicPrice: DynamicPriceResultSource | null | undefined,
): boolean {

    // v2: every group is a condition, and the engine synthesizes exactly one `isImplicitFallback`
    // row when none of them matched. An empty `groups` array configures no condition at all.
    if (shape?.groupCount != null) {
        const evaluated = dynamicPrice?.ruleCalculations?.groups;

        return shape.groupCount > 0
            && evaluated?.length > 0
            && evaluated.every(group => group.isImplicitFallback);
    }

    // Legacy: `ruleIndex` is the engine's own "a rule set matched" signal, null when none did. A
    // formula that produced rows priced the position on its own, so the fallback never stood alone.
    return shape?.ruleSetCount > 0
        && dynamicPrice?.ruleIndex == null
        && !(dynamicPrice?.ruleCalculations?.calculations?.length > 0);
}

/**
 * Whether the result's price comes from a rule: a matched legacy rule set, a formula, or a matched v2
 * group. v2 sets no ruleIndex and usually no formula rows, so checking only those two reset a v2 rule
 * price (0 € included) to the catalog price in the Bierdeckel dialog (#7140).
 */
export function isRulePricedResult(dynamicPrice: DynamicPriceResultSource | null | undefined): boolean {
    return (dynamicPrice?.ruleIndex != null && dynamicPrice.ruleIndex >= 0)
        || dynamicPrice?.ruleCalculations?.calculations?.length > 0
        || !!dynamicPrice?.ruleCalculations?.groups?.some(group => !group.isImplicitFallback);
}

/**
 * Whether the rule engine has anything to work with. `groups != null` signals the v2 engine even
 * with an empty array — an all-conditions-false product still resolves to its base price there.
 */
export function hasEvaluableRules(product: DynamicPriceProductSource | null | undefined): boolean {
    const ruleMap = product?.priceRuleMap;

    return ruleMap?.ruleSets?.length > 0
        || ruleMap?.calculationSets?.length > 0
        || ruleMap?.groups != null;
}
